import { isRu } from "./../../helpers/index"
import { apolloClient, mutation, query, queryIList, simpleMutation } from "../../api"
import { Commit, Dispatch, ActionTree } from "vuex"
import { IPrompt } from "@/types"
import { generateSimpleQuery } from "@/helpers/introspection-query"
import { toast } from "vue3-toastify"
import { Getters } from "../getters"
import { queryfy } from "@/helpers/api"
import ls from "local-storage"
import { CreateCompletionRequest } from "openai"

export interface IList<T> {
    items: T[]
    count: number
}
export interface State {
    prompts: IList<IPrompt>
    promptsLoading: boolean
    myPrompts: IList<IPrompt>
    favorites: IPrompt[]
}

export interface VarValues {
    field: string
    value: string
}

// Helper functions
const handleQueryError = (e: any, message: string) => {
    toast(`${message} failed: ${e}`, { type: "error", position: "bottom-right", theme: "colored" })
    throw new Error(`${message} failed: ${e}`)
}

const fetchPrompts = async (queryName: string, params: {[key: string]: any}) => {
    const sort = ["runned:desc", "opened:desc"]
    return await queryIList(queryName, ["id", "name", "content", "categories_ids", "userId"], { ...params, language: isRu() ? "ru" : undefined, sort })
}

export default {
    state() {
        const state = {
            prompts: {},
            promptsLoading: false,
            myPrompts: {},
            favorites: [] as IPrompt[],
        } as State
        return state
    },
    mutations: {
        SET_PROMPTS: (state: State, prompts: IList<IPrompt>) => {
            state.prompts = prompts
        },
        SET_MY_PROMPTS: (state: State, prompts: IList<IPrompt>) => {
            state.myPrompts = prompts
        },
        SET_FAVORITES: (state: State, prompts: IPrompt[]) => {
            state.favorites = prompts
        },
        ADD_PROMPTS: (state: State, prompts: IList<IPrompt>) => {
            state.prompts.items.push(...prompts.items)
            state.prompts.count = prompts.count
        },
        ADD_MY_PROMPTS: (state: State, prompts: IList<IPrompt>) => {
            state.myPrompts.items.push(...prompts.items)
            state.myPrompts.count = prompts.count
        },
        SET_PROMPTS_LOADING: (state: State, promptsLoading: boolean) => {
            state.promptsLoading = promptsLoading
        },
        ADD_MY_PROMPT: (state: State, prompt: IPrompt) => {
            state.myPrompts.items.unshift({ ...prompt })
            state.myPrompts.count++
        },
        ADD_FAVORITE: (state: State, prompt: IPrompt) => {
            if (state.favorites) {
                state.favorites.unshift({ ...prompt })
            } else {
                state.favorites = [{ ...prompt }]
            }
        },
        ADD_PROMPT: (state: State, prompt: IPrompt) => {
            state.prompts.items.unshift({ ...prompt })
            state.prompts.count++
        },
        DEL_MY_PROMPT: (state: State, id: string) => {
            state.myPrompts.items = state.myPrompts.items.filter((prompt) => prompt.id !== id)
            state.myPrompts.count--
        },
        DEL_FAVORITE: (state: State, id: string) => {
            state.favorites = state.favorites.filter((prompt) => prompt.id !== id)
        },
        DEL_PROMPT: (state: State, id: string) => {
            state.prompts.items = state.prompts.items.filter((prompt) => prompt.id !== id)
            state.prompts.count--
        },
        EDIT_PROMPT: (state: State, input: {id: string, field: string, value: any}) => {
            const prompt = state.prompts.items.find((prompt) => prompt.id === input.id)
            if (prompt) {
                (prompt as any)[input.field] = input.value
            }
        },
    },
    actions: {
        async getPrompt({ commit }: { commit: Commit }, id: string) {
            try {
                const res = await query("prompts", ["id", "name", "content", "categories_ids", "userId"], { id })
                commit("SET_PROMPTS", [res])
            } catch (e) {
                handleQueryError(e, "getPrompts")
            }
        },
        async addPrompts({ commit, getters }: { commit: Commit; getters: Getters }, { categoryId, start, q }: { categoryId?: string; start: number; q?: string }) {
            try {
                const res = await fetchPrompts("promptsAllAndCount" + (getters.user ? "User" : ""), { categoryId, start, q })
                commit("ADD_PROMPTS", res)
            } catch (e) {
                handleQueryError(e, "getPrompts")
            }
        },
        async addMyPrompts({ commit, getters }: { commit: Commit; getters: Getters }, { byUser, start, q }: { byUser?: string; start: number; q?: string }) {
            try {
                const res = await fetchPrompts("promptsAllAndCount" + (getters.user ? "User" : ""), { byUser: true, start, q })
                commit("ADD_MY_PROMPTS", res)
            } catch (e) {
                handleQueryError(e, "getMyPrompts")
            }
        },

        async getMyPrompts({ commit, getters }: { commit: Commit; getters: Getters }, { categoryId, start, byUser, q }: { categoryId?: string; start: number; byUser?: boolean; q?: string }) {
            try {
                const res = await fetchPrompts("promptsAllAndCount" + (getters.user ? "User" : ""), { categoryId, start, byUser: true, q })
                commit("SET_MY_PROMPTS", res)
            } catch (e) {
                handleQueryError(e, "getMyPrompts")
            }
        },

        async getFavorites({ commit, getters }: { commit: Commit; getters: Getters }, { categoryId, start, byUser, byIds }: { categoryId?: string; start: number; byUser?: boolean; byIds: string[] }) {
            try {
                const res = await query("promptsAll" + (getters.user ? "User" : ""), ["id", "name", "content", "categories_ids", "userId"], { categoryId, start, byUser, byIds })
                commit("SET_FAVORITES", res)
            } catch (e) {
                handleQueryError(e, "getFavorites")
            }
        },

        async getPrompts({ commit, getters }: { commit: Commit; getters: Getters }, { categoryId, start, byUser, q }: { categoryId?: string; start: number; byUser?: boolean; q?: string }) {
            try {
                commit("SET_PROMPTS_LOADING", true)
                const res = await fetchPrompts("promptsAllAndCount" + (getters.user ? "User" : ""), { categoryId, start, byUser, q })
                commit("SET_PROMPTS", res)
            } catch (e) {
                handleQueryError(e, "getPrompts")
            } finally {
                commit("SET_PROMPTS_LOADING", false)
            }
        },

        async addPrompt({ commit }: { commit: Commit }, { name, categories_ids, content }: { name: string; categories_ids: string[]; content: string }) {
            try {
                const res = await mutation("createPrompts", { name, categories_ids, content }, ["id", "name", "content", "categories_ids", "userId"])
                console.log("createPrompt", res)
                commit("ADD_PROMPT", res)
                return res
            } catch (e) {
                handleQueryError(e, "createPrompt")
            }
        },

        async editPrompt({ commit }: { commit: Commit }, input: { id: string; field: string; value: any }) {
            try {
                commit("EDIT_PROMPT", input)
                const res = await mutation("updatePrompts", { _id: input.id, [input.field]: input.value }, ["id", "name"])
            } catch (e) {
                handleQueryError(e, "editPrompt")
            }
        },
        async delPrompt({ commit } : {commit: Commit}, id: string) {
            try {
                const res = await simpleMutation("deletePrompts", "_id", id)
                console.log("delPrompt", res)
                commit("DEL_PROMPT", id)
            } catch (e) {
                handleQueryError(e, "delPrompt")
            }
        },
        async sendOpenedPrompt({ commit } : {commit: Commit}, id: string) {
            try {
                const res = await simpleMutation("incrementOpenedField", "id", id)
                console.log("sendOpenedPrompt", res)
            } catch (e) {
                handleQueryError(e, "sendOpenedPrompt")
            }
        },
        async sendRunnedPrompt({ commit } : {commit: Commit}, id: string) {
            try {
                const res = await simpleMutation("incrementRunnedField", "id", id)
                console.log("sendRunnedPrompt", res)
            } catch (e) {
                handleQueryError(e, "sendRunnedPrompt")
            }
        },
        async runPlayground({ commit, dispatch, getters } : {commit: Commit, dispatch: Dispatch, getters: Getters}, prompt: string) {
            try {
                const settings = (ls as any)("gptSettings") || {}
                if (getters.user) {
                    const res = await mutation("run", { prompt, settings }, [], { varQuery: `prompt: "${prompt}", settings: ${queryfy(settings)}` })
                    return res
                } else {
                    const guestId = (ls as any)("guestId") || ""
                    const res = await mutation("runGuest", { prompt, guestId }, [], { varQuery: `prompt: "${prompt}", guestId: "${guestId}", settings: ${queryfy(settings)}` })
                    return res
                }
            } catch (e) {
                handleQueryError(e, "run")
            }
        },
        async runPrompt({ commit, dispatch, getters } : {commit: Commit, dispatch: Dispatch, getters: Getters}, opts: {promptId: string, varValues: VarValues[], settings?: Partial<CreateCompletionRequest>}) {
            try {
                const settings = {...((ls as any)("gptSettings") || {}), ...(opts?.settings || {})}
                if (getters.user) {
                    const res = await mutation("runPrompt", { ...opts, settings }, [], { varQuery: `promptId: "${opts.promptId}", varValues: ${queryfy(opts.varValues)}, settings: ${queryfy(settings)}` })
                    return res
                } else {
                    const guestId = (ls as any)("guestId") || ""
                    const res = await mutation("runPromptGuest", { guestId, promptId: opts.promptId, varValues: opts.varValues, settings }, [], { varQuery: `promptId: "${opts.promptId}", varValues: ${queryfy(opts.varValues)}, guestId: "${guestId}", settings: ${queryfy(settings)}` })
                    return res
                }
            } catch (e) {
                handleQueryError(e, "run")
            }
        },
    }
}
